<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import type { Dayjs } from 'dayjs'
import { timeOptions, type TimePanelOptions } from './time'

const props = withDefaults(defineProps<TimePanelOptions & { value: Dayjs }>(), { format: 'HH:mm:ss', hourStep: 1, minuteStep: 1, secondStep: 1, hideDisabledOptions: false })
const emit = defineEmits<{ 'update:value': [value: Dayjs] }>()
const panelRef = ref<HTMLElement | null>(null)
const columns = computed(() => [
  { key: 'hour' as const, label: '小时', enabled: /[Hh]/.test(props.format), values: timeOptions(24, props.hourStep), blocked: props.disabledHours?.() ?? [] },
  { key: 'minute' as const, label: '分钟', enabled: /m/.test(props.format), values: timeOptions(60, props.minuteStep), blocked: props.disabledMinutes?.(props.value.hour()) ?? [] },
  { key: 'second' as const, label: '秒', enabled: /s/.test(props.format), values: timeOptions(60, props.secondStep), blocked: props.disabledSeconds?.(props.value.hour(), props.value.minute()) ?? [] }
].filter(column => column.enabled))
function choose(key: 'hour' | 'minute' | 'second', value: number) {
  const column = columns.value.find(column => column.key === key)!
  if (!column.blocked.includes(value)) emit('update:value', props.value.set(key, value))
}
async function keydown(event: KeyboardEvent) {
  if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return
  event.preventDefault()
  const target = event.target as HTMLElement
  const buttons = Array.from(target.closest('ul')?.querySelectorAll<HTMLButtonElement>('button:not([disabled])') ?? [])
  const index = buttons.indexOf(target as HTMLButtonElement)
  const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : Math.max(0, Math.min(buttons.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)))
  buttons[next]?.focus()
}
watch(() => props.value, async () => { await nextTick(); for (const selected of Array.from(panelRef.value?.querySelectorAll<HTMLElement>('[aria-selected="true"]') ?? [])) selected.scrollIntoView({ block: 'nearest' }) }, { immediate: true })
</script>

<template>
  <div ref="panelRef" class="rr-time-panel">
    <ul v-for="column in columns" :key="column.key" role="listbox" :aria-label="column.label" @keydown="keydown">
      <li v-for="option in column.values.filter(value => !hideDisabledOptions || !column.blocked.includes(value))" :key="option" role="presentation"><button type="button" role="option" :aria-selected="value.get(column.key) === option" :disabled="column.blocked.includes(option)" :tabindex="value.get(column.key) === option ? 0 : -1" @click="choose(column.key, option)">{{ String(option).padStart(2, '0') }}</button></li>
    </ul>
  </div>
</template>

<style scoped>
.rr-time-panel { display: flex; padding: var(--rr-space-2); gap: var(--rr-space-1); }
.rr-time-panel ul { box-sizing: border-box; list-style: none; min-width: 64px; max-height: 224px; margin: 0; padding: 0; overflow-y: auto; overscroll-behavior: contain; }
.rr-time-panel li { margin: 0; padding: 0; }
.rr-time-panel button { width: 100%; min-height: 28px; border: 0; padding: var(--rr-space-1); border-radius: var(--rr-radius-sm); color: var(--rr-color-text); background: transparent; cursor: pointer; font: inherit; }
.rr-time-panel button[aria-selected="true"] { color: var(--rr-color-primary); background: var(--rr-color-primary-soft); }
.rr-time-panel button:hover:not(:disabled) { background: var(--rr-color-surface-hover); }
.rr-time-panel button:disabled { color: var(--rr-color-text-disabled); cursor: not-allowed; }
</style>
